import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Button } from "@/components/design/Button";
import { OrganizationalPowerTraitList } from "@/pages/organizational-record/OrganizationalPowerTraitList";
import type { ImportPowerRow } from "./use-import-power-analysis";

/**
 * The power-analysis part of one roster-import result row: where the analysis stands, and once a
 * proposal is ready its selection checkbox and the proposed values to review before applying.
 */
export function ImportPowerResult({ index, row, selected, onToggle, disabled, subject }: {
  index: number; row: ImportPowerRow; selected: boolean; onToggle: () => void; disabled: boolean; subject: string;
}) {
  const intl = useIntl();
  const [open, setOpen] = useState(false);
  const id = `cv-import-power-${index}`;
  const working = row.status === "queued" || row.status === "filing" || row.status === "running" || row.status === "applying";
  const traits = row.status === "ready" || row.status === "applying" || row.status === "applied" ? row.analysis.traits : [];
  const error = (row.status === "failed" || row.status === "ready") && row.error ? row.error : null;

  // Per-item rows carry their state in words, never in red (UI_CONVENTIONS, danger colour).
  return <>
    <span id={id} data-status={row.status} className="inline-flex items-center gap-2 text-ink">
      {row.status === "ready" && <input id={`cv-import-power-select-${index}`} type="checkbox" checked={selected} disabled={disabled}
        onChange={onToggle} className="h-4 w-4 shrink-0 accent-ink disabled:opacity-30"
        aria-label={intl.formatMessage({ id: "service.page.bulkCv.powerSelect", defaultMessage: "Select the power proposal for {name}" }, { name: subject })} />}
      {working && <span aria-hidden className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent" />}
      <StatusText row={row} count={traits.length} />
    </span>
    {traits.length > 0 && <Button id={`cv-import-power-details-${index}`} variant="ghost" size="sm" aria-expanded={open} onClick={() => setOpen(value => !value)}>
      <FormattedMessage id={open ? "service.page.bulkCv.powerHideDetails" : "service.page.bulkCv.powerDetails"} />
    </Button>}
    {error && <span id={`cv-import-power-error-${index}`} className="basis-full text-ink">{error}</span>}
    {open && traits.length > 0 && <OrganizationalPowerTraitList id={`cv-import-power-traits-${index}`} traits={traits} className="box-flat basis-full bg-page px-3" />}
  </>;
}

function StatusText({ row, count }: { row: ImportPowerRow; count: number }) {
  switch (row.status) {
    case "queued": return <FormattedMessage id="service.page.bulkCv.powerQueued" defaultMessage="Power analysis waiting" />;
    case "filing": return <FormattedMessage id="service.page.bulkCv.powerFiling" defaultMessage="Filing in the organizational record …" />;
    case "running": return <FormattedMessage id="service.page.bulkCv.powerRunning" defaultMessage="Power analysis running …" />;
    case "ready": return <FormattedMessage id="service.page.bulkCv.powerReady" defaultMessage="{n, plural, one {# power proposed} other {# powers proposed}}" values={{ n: count }} />;
    case "empty": return <FormattedMessage id="service.page.bulkCv.powerEmpty" defaultMessage="No powers found" />;
    case "applying": return <FormattedMessage id="service.page.bulkCv.powerApplying" defaultMessage="Applying …" />;
    case "applied": return <FormattedMessage id="service.page.bulkCv.powerApplied" defaultMessage="{n, plural, one {# power applied} other {# powers applied}}" values={{ n: count }} />;
    case "unsupported": return <FormattedMessage id="service.page.bulkCv.powerUnsupported" defaultMessage="No power analysis: file type not supported" />;
    case "failed": return <FormattedMessage id="service.page.bulkCv.powerFailed" defaultMessage="Power analysis failed" />;
  }
}
